// The real night sky: catalogue stars and the moon, placed for the resort's latitude/longitude and the map's clock.
// Stars: web/sky/stars.bin (Yale Bright Star Catalogue via NASA HEASARC, a U.S. Government Work: free for any use,
// no credit needed; built by tools/sky/build_stars.py), ~5000 stars to magnitude 6, one draw call. Each star is a
// fixed 1-3 screen-pixel soft dot at any zoom, kept under the bloom threshold (no blurry blobs), dimmed and lost
// towards the horizon by the air (extinction), coming out brightest-first as the twilight deepens and fewer under a
// bright moon. The sky turns with sidereal time, so stars, sun and moon always agree with the map's date and hour.
// Moon: position (sun.js moonPosition), phase from the sun direction (drawn in the sky shader), and the direction and
// strength of the night's moonlight.
// Every resort gets it (owner approved the "soft" look on 2026-10-15). resort.json "sky" only overrides:
// { "stars": "<look id>" | { look, ...overrides } | false, "moon": true | { "scale", "light" } | false }; "sky": false = none.
import * as THREE from 'three/webgpu';
import { uniform, float, vec2, vec3, vec4, mix, pow, max, min, clamp, exp, sin, dot, normalize, step, select, varying, positionGeometry, instancedBufferAttribute, modelWorldMatrix, modelViewMatrix, cameraProjectionMatrix, viewportSize } from 'three/tsl';
import { siderealDeg, moonPosition, sunVector } from './sun.js';
import { degraded } from './degraded.js'; // VIS-27: a quiet failure is reported

const RAD = Math.PI / 180;

// Star looks (soft = the default, approved 2026-10-15; fine and rich were the other preview options). size: px of a magnitude-limit star (bright stars up to x2); gain: brightness of the
// brightest star (x the bloom-safe cap); limit: faintest magnitude seen on a dark moonless night; tint: B-V colour
// strength; twinkle: scintillation amount, near the horizon only (stars overhead hold steady).
export const STAR_LOOKS = {
  fine: { size: 1.5, gain: 0.8, limit: 5.6, tint: 0.45, twinkle: 0 },
  soft: { size: 1.9, gain: 0.95, limit: 5.8, tint: 0.65, twinkle: 0.12 },
  rich: { size: 2.3, gain: 1, limit: 6, tint: 0.8, twinkle: 0.2 },
};

// The moonlight comes from the moon's compass bearing but never from lower than 32° (about a winter midnight moon at
// 68°N). Owner, 2026-10-26: at 03:00 on 21 Dec the moon sinks in the west and lit the slopes facing it square on
// while the flat snow went dark: the cabins' roofs glowed (roof 2.7x the snow, 2.0x at midnight). The moonlight
// casts no shadow, so its height is never seen; the moon disc stays where it is.
export const MOON_LIGHT_MIN_ALT = 32;
const MOON_LIGHT_TAN = Math.tan(MOON_LIGHT_MIN_ALT * RAD);
/** the moonlight's direction (unit vector into `out`) for the moon at `dir` (unit, y up) */
export function moonLightDir(dir, out) {
  const h = Math.hypot(dir.x, dir.z);
  out.copy(dir);
  if (dir.y < h * MOON_LIGHT_TAN) out.setY(h * MOON_LIGHT_TAN);
  return out.normalize();
}

// WS-75 sky: the stars as one instanced draw of small quads (WebGPU draws points 1 px wide only). Per star: its
// position on the sphere, magnitude, B-V colour and a seed; the quad is sized in screen pixels
// and sits on the far plane, like the sky dome.
function starMaterial(g, U) {
  const pos = instancedBufferAttribute(g.userData.pos), mag = instancedBufferAttribute(g.userData.mag);
  const bv = instancedBufferAttribute(g.userData.bv), seed = instancedBufferAttribute(g.userData.seed);
  const dir = normalize(modelWorldMatrix.mul(vec4(pos, 0)).xyz);
  const s = max(dir.y, 0);
  const airmass = float(1).div(s.add(exp(s.mul(-11)).mul(0.025)));                    // ~1 overhead, ~40 at the horizon
  const m = mag.add(airmass.sub(1).mul(0.22));                                           // extinction, magnitudes
  const v = clamp(U.limit.sub(m).div(6), 0, 1);                                          // 0 at the limit, 1 for the brightest
  const s1 = float(1).sub(s);
  const tw = float(1).add(U.twinkle.mul(s1).mul(s1).mul(sin(U.time.mul(seed.mul(4).add(5)).add(seed.mul(40)))).mul(sin(U.time.mul(2.3).add(seed.mul(17)))));
  const hid = step(U.moonCos, dot(dir, U.moonDir));                                      // behind the moon's disc
  const I = min(U.gain.mul(select(v.greaterThan(0), pow(v, 0.75), 0)).mul(tw), 1).mul(U.cap).mul(U.fade).mul(float(1).sub(hid)).mul(step(0, dir.y));
  const c = select(bv.lessThan(0.4), mix(vec3(0.72, 0.82, 1.0), vec3(1.0), clamp(bv.add(0.3).div(0.7), 0, 1)),
    mix(vec3(1.0), vec3(1.0, 0.72, 0.45), clamp(bv.sub(0.4).div(1.2), 0, 1)));
  const size = select(I.greaterThan(0), U.size.mul(v.mul(v).add(1)).mul(U.dpr), 0);    // diameter, device pixels
  const clip = cameraProjectionMatrix.mul(modelViewMatrix.mul(vec4(pos, 1)));
  const off = positionGeometry.xy.mul(size).div(max(viewportSize, vec2(1))).mul(clip.w);
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false, lights: false }); // WS-77 lights: points of light (the basic lighting gave diffuse x 1: the same colour)
  mat.vertexNode = vec4(clip.xy.add(off), clip.w, clip.w);                              // on the far plane, like the sky dome
  const vCol = varying(mix(vec3(1.0), c, U.tint).mul(I), 'vStarCol');
  const p = positionGeometry.xy;                                                         // -1..1 across the dot
  mat.colorNode = vCol.mul(max(exp(dot(p, p).mul(-2.2)).sub(0.1), 0));                  // soft round dot, no square edge
  return mat;
}

async function loadStars(url) {
  const buf = await fetch(url).then((r) => { if (!r.ok) throw new Error(`${url}: ${r.status}`); return r.arrayBuffer(); });
  const dv = new DataView(buf);
  if (dv.getUint32(0) !== 0x53544152) throw new Error(`${url}: not a star file`);
  const n = dv.getUint16(6, true);
  const pos = new Float32Array(n * 3), mag = new Float32Array(n), bv = new Float32Array(n), seed = new Float32Array(n);
  for (let i = 0, o = 8; i < n; i++, o += 6) {
    const ra = (dv.getUint16(o, true) / 65535) * 2 * Math.PI, dec = (dv.getInt16(o + 2, true) / 32767) * Math.PI / 2;
    // equatorial unit vector, x -> RA 0h, y -> RA 6h, z -> north celestial pole (x1000: inside any far plane)
    pos.set([1000 * Math.cos(dec) * Math.cos(ra), 1000 * Math.cos(dec) * Math.sin(ra), 1000 * Math.sin(dec)], i * 3);
    mag[i] = dv.getUint8(o + 4) / 30 - 2;
    bv[i] = dv.getInt8(o + 5) / 50;
    seed[i] = Math.abs(Math.sin(i * 12.9898) * 43758.5453) % 1;
  }
  // WS-75 sky: one quad (two triangles) per star, instanced
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0], 3));
  g.setIndex([0, 1, 2, 0, 2, 3]);
  g.instanceCount = n;
  g.userData = { pos: new THREE.InstancedBufferAttribute(pos, 3), mag: new THREE.InstancedBufferAttribute(mag, 1), bv: new THREE.InstancedBufferAttribute(bv, 1), seed: new THREE.InstancedBufferAttribute(seed, 1) };
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1000);
  return g;
}

const shortest = (a, b) => ((b - a + 540) % 360) - 180;

/**
 * cfg = resort.sky (overrides of the defaults); sets skyUniforms moonDir/moonK/moonSize/moonHalo (declared in main.js). Null when switched off.
 * set(ms, lat, lon, k, s, sunDir): aim at a time (k < 1 = mid-glide from the pose at hold()); frame(camera, dpr, t, cap).
 */
export function createNightSky({ cfg: given, scene, skyUniforms }) {
  if (given === false) return null;
  const cfg = { stars: 'soft', moon: true, ...(given || {}) };
  if (!cfg.stars && !cfg.moon) return null;
  const starCfg = cfg.stars ? (typeof cfg.stars === 'string' ? { look: cfg.stars } : cfg.stars) : null;
  const moonCfg = cfg.moon ? (cfg.moon === true ? {} : cfg.moon) : null;
  const look = starCfg ? { ...STAR_LOOKS.soft, ...(STAR_LOOKS[starCfg.look] || {}), ...starCfg } : null;
  const moonScale = moonCfg?.scale ?? 1.6;                               // x the true size (0.26 deg radius)
  skyUniforms.moonSize.value = 0.0045 * moonScale;
  const U = { // WS-75 sky: TSL uniform nodes (moonDir is the sky's own)
    limit: uniform(6), size: uniform(2), gain: uniform(1), cap: uniform(0.7), tint: uniform(0.6),
    twinkle: uniform(0), time: uniform(0), dpr: uniform(1), fade: uniform(0),
    moonDir: skyUniforms.moonDir, moonCos: uniform(Math.cos(0.0045 * moonScale)),
  };
  let points = null;
  if (look) {
    loadStars(new URL('../sky/stars.bin', import.meta.url).href).then((g) => {
      points = new THREE.Mesh(g, starMaterial(g, U)); // WS-75 sky: instanced quads (was THREE.Points)
      points.matrixAutoUpdate = false;
      points.frustumCulled = false;
      points.userData.notGeometry = true; // WS-75 bakes: points of light, not things: the Renders tool leaves them out of its class and depth maps (as THREE.Points were)
      points.renderOrder = -0.5;                                         // after the sky dome, before everything else
      points.visible = false;
      scene.add(points);
    }).catch((e) => { console.warn('nightsky: stars not loaded', e); degraded('stars', e); });
  }
  const R = new THREE.Matrix4(), moonVec = new THREE.Vector3();
  let lst = null, from = null, night = 0;
  const moon = { dir: moonVec, alt: -90, illum: 0, up: 0 };

  return {
    stars: !!look, moon,
    hold() { if (lst !== null) from = { lst, dir: moonVec.clone() }; },
    set(ms, lat, lon, k, s, sunDir) {
      const L = siderealDeg(ms, lon), mp = moonPosition(ms, lat, lon), to = sunVector(mp, new THREE.Vector3());
      if (k < 1 && from) { lst = (from.lst + shortest(from.lst, L) * k + 360) % 360; moonVec.copy(from.dir).lerp(to, k).normalize(); }
      else { lst = L; from = k < 1 ? from : null; moonVec.copy(to); }
      // equatorial (x 0h, y 6h, z pole) -> scene (x east, y up, z south) at latitude phi and sidereal angle lst
      const l = lst * RAD, p = lat * RAD, cl = Math.cos(l), sl = Math.sin(l), cp = Math.cos(p), sp = Math.sin(p);
      R.set(-sl, cl, 0, 0, cp * cl, cp * sl, sp, 0, sp * cl, sp * sl, -cp, 0, 0, 0, 0, 1);
      night = s.night;
      moon.alt = Math.asin(THREE.MathUtils.clamp(moonVec.y, -1, 1)) / RAD;
      moon.illum = (1 - moonVec.dot(sunDir)) / 2;                       // lit fraction from the sun-moon angle
      moon.up = THREE.MathUtils.clamp((moon.alt + 0.5) / 4, 0, 1);
      if (moonCfg) {
        skyUniforms.moonDir.value.copy(moonVec);
        skyUniforms.moonK.value = moon.up * Math.min(1, moon.illum * 3) * THREE.MathUtils.lerp(0.3, 1, night);
        skyUniforms.moonHalo.value = moon.up * moon.illum * moon.illum * 0.05 * night;
      }
      if (look) {
        // the faintest star seen: ~1.5 in bright twilight to the look's limit when dark; a full moon up takes ~1.3 off
        U.limit.value = THREE.MathUtils.lerp(1.5, look.limit, night) - (moonCfg ? 1.3 * moon.illum * moon.up : 0);
        U.fade.value = THREE.MathUtils.smoothstep(night, 0, 0.35);
      }
    },
    /** WS-77: the stars' largest dot radius (CSS px; a star's quad reaches that far from its point) and a switch that
     *  hides them for this frame (main.js: the whole view is below the horizon, where no star is drawn) */
    starPx: look ? look.size + 1 : 0,
    cull(off) { if (off && points) points.visible = false; },
    /** QA / warm start: the star mesh once loaded (null before) */
    get mesh() { return points; },
    /** moonlight: { dir, k } with k 0..1 (0 = no moon up, 1 = full moon high) for the night light. */
    light() { return { dir: moonVec, k: moon.up * moon.illum * THREE.MathUtils.clamp(moonVec.y * 3, 0, 1) }; },
    moonCfg,
    frame(camera, dpr, t, cap) {
      if (!points) return;
      points.visible = night > 0.001;
      if (!points.visible) return;
      points.matrix.copy(R).setPosition(camera.position);
      points.matrixWorldNeedsUpdate = true;
      U.size.value = look.size; U.gain.value = look.gain; U.tint.value = look.tint; U.twinkle.value = look.twinkle;
      U.time.value = t; U.dpr.value = dpr; U.cap.value = cap;
    },
  };
}
